/**
 * Clipboard helpers with feature detection + legacy fallback.
 * `navigator.clipboard` requires HTTPS (or localhost) and user-gesture
 * context. We cover the edges: file://, sandboxed iframes, old Safari iOS.
 */

/**
 * Copy `text` to the system clipboard. Returns true on success, false if
 * every available path failed. Safe to call without awaiting.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Preferred path — modern async API.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand fallback (permissions denied, insecure origin).
    }
  }

  // Legacy fallback — works on iOS Safari < 13.4 and non-secure origins.
  if (typeof document === 'undefined') return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // Keep it off-screen and style-neutral so it doesn't shift layout or
  // trigger keyboards on mobile.
  ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
  document.body.appendChild(ta);
  try {
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand('copy');
    return ok;
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Wire an element so clicking / tapping it copies `opts.text` (or its own
 * textContent) to the clipboard. Shows brief inline feedback ("Скопировано!")
 * and a subtle haptic buzz on supporting devices.
 *
 * Returns a disposer — call it to remove all listeners and any pending
 * feedback timer.
 */
export function wireClickToCopy(
  el: HTMLElement,
  opts: { label: string; text?: string; feedback?: string } = { label: '' }
): () => void {
  const label = opts.label || 'значение';
  const feedback = opts.feedback || 'Скопировано!';

  el.style.cursor = 'pointer';
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('aria-label', `Скопировать ${label}`);
  el.setAttribute('title', `Нажмите, чтобы скопировать ${label}`);

  let feedbackTimer: ReturnType<typeof setTimeout> | null = null;
  let originalText: string | null = null;

  const showFeedback = () => {
    if (originalText === null) originalText = el.textContent;
    el.textContent = feedback;
    if (feedbackTimer) clearTimeout(feedbackTimer);
    feedbackTimer = setTimeout(() => {
      if (originalText !== null) el.textContent = originalText;
      originalText = null;
      feedbackTimer = null;
    }, 1200);
  };

  const doCopy = async () => {
    const text = opts.text ?? originalText ?? el.textContent ?? '';
    const ok = await copyToClipboard(text);
    if (ok) {
      showFeedback();
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(30); } catch { /* noop */ }
      }
    }
  };

  const onClick = () => { void doCopy(); };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void doCopy(); }
  };

  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKey);

  return () => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKey);
    if (feedbackTimer) clearTimeout(feedbackTimer);
    if (originalText !== null) el.textContent = originalText;
  };
}
